import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, CalendarDays, MapPin, GraduationCap, BookOpen, FileText, Network, Brain,
  Lock, Play, ShieldCheck, Clock3, CheckCircle2,
} from 'lucide-react';
import publicApi from '../lib/api';
import { privateStudyApi } from '../lib/studyApi';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { fmtDate, statusStyle, statusLabel } from '../components/ContestCard';
import { AdmCover, AdmNotice, AdmMetadata, ADM_SUBTITLE, isAdmPreparation } from '../components/AdmPreparation';

export default function ContestDetail({ preview = false }) {
  const api = preview ? privateStudyApi : publicApi;
  const routePrefix = preview ? '/admin/preview' : '';
  const { contestId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [error, setError] = useState(null);
  const [access, setAccess] = useState(null);
  const [accessAttempt, setAccessAttempt] = useState(0);
  const userId = user?.user_id;
  // Ask the same read-only endpoint used by the reader. Never infer access from
  // a role, a checkout redirect, or a locally cached purchase flag.
  const accessStatus = preview || !userId ? 'denied'
    : access?.userId === userId && access?.contestId === contestId
      ? access.status : 'checking';
  const hasAccess = accessStatus === 'granted';

  useEffect(() => {
    if (preview || !userId) { setAccess(null); return; }
    let current = true;
    setAccess({ userId, contestId, status: 'checking' });
    publicApi.get(`/library/${contestId}`).then(({ data: library }) => {
      if (current) setAccess({ userId, contestId,
        status: library.contest?.contest_id === contestId ? 'granted' : 'error' });
    }).catch(err => {
      if (current) setAccess({ userId, contestId,
        status: err.response?.status === 403 ? 'denied' : 'error' });
    });
    return () => { current = false; };
  }, [contestId, userId, preview, accessAttempt]);

  useEffect(() => {
    setError(null);
    setData(null);
    api.get(`/contests/${contestId}`)
      .then(({ data }) => setData(data))
      .catch(() => setError('Concorso non trovato'));
  }, [contestId, api]);

  const buy = async () => {
    if (preview) return;
    if (!user) {
      navigate(`/accedi?next=/concorsi/${contestId}`);
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post('/payments/checkout', {
        contest_id: contestId, ...(promoCode.trim() ? { promo_code: promoCode.trim() } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante il checkout');
      setBuying(false);
    }
  };

  if (error) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500" data-testid="contest-not-found">{error}</div>;
  if (!data) return <div className="max-w-6xl mx-auto px-4 py-24"><div className="h-96 rounded-2xl bg-slate-100 animate-pulse" /></div>;

  const { contest, index } = data;
  const comingSoon = contest.public_status === 'COMING_SOON';
  const priceReady = typeof contest.price === 'number' && Number.isFinite(contest.price) && contest.price > 0;
  const profileBlocked = contest.profile_code && !['READY', 'READY_WITH_DECLARED_LIMIT'].includes(contest.profile_commercial_status);
  const directory = contest.offer_kind === 'PROFILE_DIRECTORY';
  const studyMinutes = index.total_pages ? Math.max(30, Math.round(index.total_pages * 7.5)) : null;
  const studyTime = studyMinutes ? (studyMinutes >= 60 ? `~${Math.floor(studyMinutes / 60)}h ${studyMinutes % 60 ? `${studyMinutes % 60}m` : ''}` : `~${studyMinutes}m`) : null;

  return (
    <main className="page-shell py-8 sm:py-12" data-testid="contest-detail-page">
      <AdmMetadata contest={contest} />
      {preview && <div role="status" className="mb-6 rounded-xl bg-amber-100 p-4">
        <p>Anteprima privata admin · Corso non pubblicato · Nessun acquisto</p>
        <Button className="mr-3 mt-3" onClick={() => navigate(`/admin/preview/reader/${contestId}`)}>Apri reader</Button>
        <Button onClick={() => navigate(`/admin/preview/quiz/${contestId}`)}>Apri quiz</Button>
      </div>}
      <div className="grid lg:grid-cols-[1fr_360px] gap-10 xl:gap-14">
        <div className="min-w-0">
          <AdmCover contest={contest} className="mb-6 rounded-2xl" />
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className={`${statusStyle(contest.status)} border`} data-testid="detail-status">{comingSoon ? 'In preparazione' : statusLabel(contest.status)}</Badge>
            <Badge variant="secondary" data-testid="detail-category">{contest.category}</Badge>
            {(contest.subjects || []).map((s) => <Badge key={s} variant="outline" className="text-slate-600">{s}</Badge>)}
          </div>
          <p className="mb-3 text-sm font-bold text-brand">{contest.ente}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-[-0.045em] font-bold text-brand-dark mb-4" data-testid="detail-title">
            {contest.title}
          </h1>
          {isAdmPreparation(contest) && <p className="mb-4 text-lg font-semibold text-brand-dark">{ADM_SUBTITLE}</p>}
          <AdmNotice contest={contest} />
          <p className="text-slate-600 text-base md:text-lg leading-8 mb-8 max-w-3xl" data-testid="detail-description">{contest.short_description}</p>
          {data.profile_offers?.length > 0 && <section className="mb-8" aria-label="Percorsi per profilo">
            <label htmlFor="profile-offer" className="block font-semibold mb-2">Scegli il tuo profilo</label>
            <select id="profile-offer" defaultValue="" className="w-full rounded border p-3"
              onChange={(event) => event.target.value && navigate(`/concorsi/${event.target.value}`)}>
              <option value="" disabled>Seleziona un percorso</option>
              {data.profile_offers.map((offer) => <option key={offer.contest_id} value={offer.contest_id}>
                {offer.profile_code} — {offer.title}{offer.public_status !== 'PUBLISHED' ? ' — In preparazione' : ''}
              </option>)}
            </select>
          </section>}

          <section className="mb-10 rounded-3xl bg-brand-dark p-6 text-white sm:p-8" aria-labelledby="essentials-path-title">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-300">Il percorso Essentials</p>
            <h2 id="essentials-path-title" className="mt-2 font-display text-2xl font-bold">Sai cosa studiare. Vedi quanto manca.</h2>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[{ icon: FileText, value: index.total_pages, label: 'pagine essenziali' }, { icon: Network, value: index.total_maps, label: 'mappe' }, { icon: Brain, value: index.quiz_count, label: 'quiz' }, { icon: Clock3, value: studyTime || '—', label: 'studio stimato' }].map((m) => <div key={m.label} className="rounded-2xl bg-white/8 p-4 ring-1 ring-white/10"><m.icon size={17} className="mb-3 text-indigo-300" /><p className="font-display text-2xl font-bold">{m.value}</p><p className="mt-1 text-xs text-slate-300">{m.label}</p></div>)}
            </div>
            {studyTime && <p className="mt-4 text-xs text-slate-400">Tempo indicativo calcolato sulla quantità di contenuto; il ritmo effettivo può variare.</p>}
          </section>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            {[
              ...(contest.positions != null ? [{ icon: Users, label: 'Posti', value: contest.positions }] : []),
              { icon: MapPin, label: 'Area', value: contest.region },
              ...(contest.deadline ? [{ icon: CalendarDays, label: 'Scadenza', value: fmtDate(contest.deadline) }] : []),
              { icon: GraduationCap, label: 'Titolo', value: contest.education_requirement || '—' },
            ].map((m) => (
              <div key={m.label} className="surface p-4">
                <m.icon size={16} strokeWidth={1.5} className="text-brand mb-2" />
                <p className="text-xs text-slate-500 uppercase tracking-wide">{m.label}</p>
                <p className="font-semibold text-slate-900 text-sm mt-0.5">{m.value}</p>
              </div>
            ))}
          </div>

          {contest.requirements && (
            <div className="mb-10">
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Requisiti</h2>
              <p className="text-slate-600 leading-relaxed">{contest.requirements}</p>
            </div>
          )}
          {contest.exam_date && (
            <p className="text-sm text-slate-500 mb-10">Data prova prevista: <span className="font-semibold text-slate-800">{fmtDate(contest.exam_date)}</span></p>
          )}

          <p className="eyebrow mb-2">Programma di studio</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark mb-5" data-testid="detail-index-title">
            Cosa studierai
          </h2>
          <Accordion type="multiple" className="space-y-3">
            {index.tree.map((m) => (
              <AccordionItem key={m.module_id} value={m.module_id} className="bg-white border border-slate-200 rounded-2xl px-5 overflow-hidden">
                <AccordionTrigger className="hover:no-underline py-5" data-testid={`module-${m.module_id}`}>
                  <div className="text-left">
                    <p className="text-xs tracking-[0.1em] uppercase text-brand font-semibold">Modulo di studio</p>
                    <p className="font-display font-semibold text-slate-900">{m.title}</p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-5">
                  {m.chapters.map((ch) => (
                    <div key={ch.chapter_id} className="mb-4">
                      <p className="text-sm font-semibold text-slate-700 mb-2">{ch.title}</p>
                      <ul className="space-y-1.5">
                        {ch.sections.map((s) => (
                          <li key={s.section_id} className="flex items-center justify-between gap-3 text-sm">
                            <span className="flex items-center gap-2 text-slate-600">
                              <FileText size={13} strokeWidth={1.5} className="text-slate-400 shrink-0" />
                              {s.title}
                              <span className="text-xs text-slate-400">· {s.pages_equiv} pag.</span>
                            </span>
                            {preview || hasAccess ? (
                              <button
                                onClick={() => navigate(`${routePrefix}/reader/${contest.contest_id}/${s.section_id}`)}
                                data-testid={`${preview ? 'preview' : 'open'}-${s.section_id}`}
                                className="text-xs font-semibold text-brand hover:underline shrink-0 inline-flex items-center gap-1">
                                <Play size={11} strokeWidth={1.5} /> {preview ? 'Anteprima' : 'Apri lezione'}
                              </button>
                            ) : accessStatus === 'denied' ? (
                              <Lock aria-label="Accesso riservato" size={12} strokeWidth={1.5} className="text-slate-300 shrink-0" />
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CARD ACQUISTO */}
        <aside className="lg:col-span-1">
          <div className="surface p-7 sticky top-24" data-testid="purchase-card">
            {hasAccess ? <div data-testid="course-access-active" className="mb-6">
              <p className="eyebrow mb-2">Accesso attivo</p>
              <p className="text-sm text-slate-600">Questo corso è già nella tua libreria.</p>
            </div> : <>
            <p className="eyebrow mb-2">Un solo acquisto</p>
            <p className="font-display text-4xl font-bold text-brand-dark" data-testid="detail-price">
              {priceReady ? `€ ${contest.price.toFixed(2)}` : 'Disponibile a breve'}
            </p>
            <p className="mb-6 mt-1 text-sm text-slate-500">Accesso al pacchetto di questo concorso</p>
            </>}
            <ul className="space-y-3 text-sm text-slate-600 mb-7">
              <li className="flex items-center gap-2.5"><BookOpen size={15} strokeWidth={1.5} className="text-brand" /> {index.total_sections} sezioni essenziali</li>
              <li className="flex items-center gap-2.5"><FileText size={15} strokeWidth={1.5} className="text-brand" /> {index.total_pages} pagine equivalenti (vs centinaia del manuale)</li>
              <li className="flex items-center gap-2.5"><Brain size={15} strokeWidth={1.5} className="text-brand" /> {index.quiz_count} quiz con spiegazioni</li>
              <li className="flex items-center gap-2.5"><Network size={15} strokeWidth={1.5} className="text-brand" /> {index.total_maps} mappe concettuali</li>
              <li className="flex items-center gap-2.5"><CheckCircle2 size={15} className="text-emerald-600" /> Acquisto singolo, nessun abbonamento</li>
              <li className="flex items-center gap-2.5"><ShieldCheck size={15} strokeWidth={1.5} className="text-brand" /> Reader in-app e progressi salvati</li>
            </ul>
            {!preview && accessStatus === 'denied' && priceReady && !comingSoon && <div className="mb-4">
              <label htmlFor="promotion-code" className="block text-sm font-medium mb-1">Codice promozionale (facoltativo)</label>
              <input id="promotion-code" value={promoCode} maxLength={100} autoComplete="off"
                onChange={(event) => setPromoCode(event.target.value)}
                className="w-full rounded border p-2" />
              <p className="text-xs text-slate-500 mt-1">Lo sconto, se valido, e il totale saranno mostrati prima del pagamento.</p>
            </div>}
            {hasAccess ? <Button onClick={() => navigate(`/reader/${contestId}`)} data-testid="continue-studying"
              className="w-full min-h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold">
              Continua a studiare
            </Button> : accessStatus === 'error' ? <div role="status">
              <p className="text-sm text-slate-600 mb-3">Impossibile verificare il tuo accesso. Riprova prima di effettuare un acquisto.</p>
              <Button variant="outline" onClick={() => setAccessAttempt(attempt => attempt + 1)}>Riprova verifica accesso</Button>
            </div> : <Button onClick={buy} disabled={preview || accessStatus === 'checking' || buying || comingSoon || !priceReady || profileBlocked || directory} data-testid="buy-button"
              className="w-full h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold transition-all hover:-translate-y-0.5">
              {preview ? 'Acquisti disabilitati in anteprima' : accessStatus === 'checking' ? 'Verifica accesso...' : comingSoon ? 'Disponibile a breve' : buying ? 'Reindirizzamento...' : user ? 'Acquista ora' : 'Accedi e acquista'}
            </Button>}
            {!hasAccess &&
            <p className="text-xs text-slate-400 mt-5 leading-relaxed">
              Pagamento sicuro con Stripe. Contenuto digitale consultabile solo in piattaforma.
            </p>}
          </div>
        </aside>
      </div>
    </main>
  );
}
